import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { constructNow, format } from 'date-fns'
import { PrimeIcons } from 'primereact/api'
import { Button } from 'primereact/button'

import { useToastContext } from '../../../../../contexts'
import { useTenantById } from '../../../../../data/tenants'
import { getHandleableError } from '../../../../../utils/errors/isXiaError'
import { getCultureFromLanguage } from '../../../../../utils/helper/getCultureFromLanguage'
import i18n from '../../../../../utils/i18n'
import { useCurrentTenantConfig } from '../../../../../utils/tenants'

export interface MasterDataDownloadButtonProps {
  /** The label for the button */
  label: string
  /** The API function that will be called - it receives the culture as parameter and returns a Blob */
  downloadFunction: (culture: string) => Promise<Blob>
  /** The filename prefix for the downloaded file (e.g. "packaging_material" or "company_codes") */
  fileNamePrefix: string
  /** The file extension (default: "csv") */
  fileExtension?: string
}

export const MasterDataDownloadButton: React.FC<
  MasterDataDownloadButtonProps
> = ({ label, downloadFunction, fileNamePrefix, fileExtension = 'csv' }) => {
  const { t, i18n } = useTranslation()
  const { replaceToast } = useToastContext()
  const { tenantId } = useCurrentTenantConfig()
  const { data: tenant } = useTenantById(tenantId as string)

  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  const handleClick = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    setIsRequestRunning(true)
    const fileName = getFilename(tenant!.name, fileNamePrefix, fileExtension)
    const culture = getCultureFromLanguage(i18n.language)

    try {
      const exportBlob = await downloadFunction(culture)

      const fileUrl = window.URL.createObjectURL(exportBlob)
      const fileLink = document.createElement('a')
      fileLink.href = fileUrl
      fileLink.setAttribute('download', fileName)
      document.body.appendChild(fileLink)
      fileLink.click()
      document.body.removeChild(fileLink)
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      replaceToast({
        style: {},
        closable: true,
        severity: 'error',
        sticky: true,
        detail: errorMessage,
        summary: t('common.errors.fileDownload', { name: fileName }),
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  return (
    <Button
      icon={PrimeIcons.DOWNLOAD}
      label={label}
      onClick={handleClick}
      loading={isRequestRunning}
      outlined
    />
  )
}

function getFilename(
  tenantName: string,
  fileNamePrefix: string,
  extension: string = 'csv',
) {
  const now = constructNow(Date.now())
  const formattedNow = format(now, 'yyyyMMddHHmmss')

  return `${formattedNow}_${fileNamePrefix}_${tenantName}_${i18n.language.toUpperCase()}.${extension}`
}
